import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { useAuthContext } from '../hooks/use-user-auth-context.hooks';
import { dispatchSignup } from '../redux/slice/auth/auth.operations';
import { SignupDTO, User } from '../redux/slice/auth/auth.types';
import { dispatchUpdateIntakeFormUserOrganization } from '../redux/slice/form-intake/form-intake.operations';
import { useAppDispatch } from '../redux/store';
import { upsertOrganization } from '../services/database/organizations';
import logger from '../services/logger';
import { hash } from '../utils/crypto';

const validatePassword = (password: unknown) => {
  if (typeof password !== 'string') {
    return false;
  }

  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*_`'^.",<>:;)(|=+,~}{?&])?[A-Za-z\d@$!%*_`'^.",<>:;)(|=+,~}{?&]{8,}$/;
  return passwordRegex.test(password);
};
const SignupSchema = yup
  .object({
    email: yup
      .string()
      .max(50, 'Email must not exceed 50 characters')
      .email('Invalid email address')
      .required(),
    password: yup
      .string()
      .max(100, 'Password must not exceed 100 characters')
      .test(
        'password-policy',
        'Password does not meet the policy, must be minimum 8 character long and should be alphanumeric',
        validatePassword,
      )
      .required(),
  })
  .required();
export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams: URLSearchParams = new URLSearchParams(location.search);
  const organization: string | undefined =
    queryParams.get('organization') ?? Cookies.get('organization');
  const disablePersonalInfo: string | undefined =
    queryParams.get('disablePersonalInfo') ?? Cookies.get('disablePersonalInfo');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(SignupSchema),
  });
  const createAcount: SubmitHandler<IFormInput> = async (dto: SignupDTO): Promise<void> => {
    logger.debug('Submitted form to register user.', dto);

    await dispatch(dispatchSignup(dto));
    if (organization) {
      const organizationHash: string = await hash(organization);
      await dispatch(dispatchUpdateIntakeFormUserOrganization({ organization: organizationHash }));
      await upsertOrganization({ organization });
      Cookies.remove('organization', { path: '/' });
    }

    toast.success('User successfully created');
    navigate('/login');
  };

  interface IFormInput {
    email: string;
    password: string;
  }

  const user: User | null = useAuthContext();
  const isAuthenticated: boolean = user ? true : false;

  useEffect(() => {
    // TODO: create utility function for this because it's used in multiple places
    if (organization) {
      Cookies.set('organization', organization, { path: '/' });
    }
  }, [organization]);

  useEffect(() => {
    if (disablePersonalInfo !== undefined && Cookies.get('disablePersonalInfo') === undefined) {
      Cookies.set('disablePersonalInfo', 'true');
    }
  }, [disablePersonalInfo]);

  return isAuthenticated ? (
    <Navigate to="/pre-employment/start" />
  ) : (
    <div className="flex items-center justify-center">
      <div
        className={`mx-auto w-full max-w-lg bg-white-100 rounded-xl p-10 border border-black/10`}
      >
        <h2 className="text-center text-2xl font-bold leading-tight mb-4">
          Sign up to create account
        </h2>
        <p className="mt-2 text-center text-base text-black/60 mb-8">
          Already have an account?&nbsp;
          <Link
            to="/login"
            className="text-purple-700 font-medium text-primary transition-all duration-200 hover:underline"
          >
            Sign In
          </Link>
        </p>
        <Box
          className="form-style"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(createAcount)}
          component="form"
          sx={{
            '& > :not(style)': { m: 1, width: '42ch' },
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="email"
            type="email"
            {...register('email')}
            label="Email"
            variant="outlined"
          />
          <p className="text-red-500">{errors.email?.message}</p>
          <TextField
            id="password"
            type="password"
            {...register('password')}
            label="Password"
            variant="outlined"
          />
          <p className="text-red-500">{errors.password?.message}</p>
          <button type="submit" className="btn-primary w-full">
            Create Account
          </button>
        </Box>
      </div>
    </div>
  );
};
